import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import RateItTile from './RateItTile';
import { RATE_IT_LAYOUT, RATE_IT_TIERS } from '../constants';

const Bar = styled.div`
  width: 100%;
  padding: ${(props) => (props.$compact ? 8 : RATE_IT_LAYOUT.BAR_PADDING_VERTICAL)}px
    ${RATE_IT_LAYOUT.BAR_GUTTER}px;
  box-sizing: border-box;
`;

const Row = styled.div`
  display: flex;
  align-items: stretch;
  gap: ${RATE_IT_LAYOUT.TILE_GAP}px;
`;

/**
 * The 3-tile sentiment bar: the brief's dedicated horizontal rating bar, thumb-zone optimised.
 *
 * `selected` and `disabled` are separate props rather than one derived flag precisely so a
 * display-only copy can render `selected={null} disabled` — a picture of the bar rather than a
 * second place to play. With the tiles filled at rest that picture is the real thing in
 * miniature, not a greyed-out stand-in, which is what lets the share screen show the mechanic
 * honestly.
 *
 * The instruction above the tiles is NOT here: it lives in `RateItFeedBottomStack`, outside the
 * tinted slab this bar sits on, because it labels the tiles without being part of them.
 *
 * Unlike mobile this component does NOT anchor itself or paint a scrim. On web the bar is always
 * placed by its owner (`RateItFeedBottomStack` on the feed, plain flow on the share screen), so
 * the two `anchored` code paths mobile needs collapse into one here.
 */
const RateItRatingBar = ({ selected, disabled, compact, onRate, tallies }) => {
  const { t } = useTranslation();

  return (
    <Bar $compact={compact}>
      {/* Mapped over the tier data, so the bar's order can never drift from the tiers. */}
      <Row
        role="group"
        aria-label={t('tiri.rateIt.caption', 'Rate this Fan Moment – Solid, Great, or Epic')}
      >
        {RATE_IT_TIERS.map((tier) => (
          <RateItTile
            key={tier.id}
            tier={tier}
            selected={selected === tier.id}
            // Derived here rather than in the tile: a tile knows only its own flag, and "nobody
            // has rated yet" must not look like "someone else was picked".
            dimmed={selected !== null && selected !== tier.id}
            disabled={disabled}
            compact={compact}
            onPress={onRate}
            count={tallies?.[tier.id]}
          />
        ))}
      </Row>
    </Bar>
  );
};

export default RateItRatingBar;
